// @flow
'use strict'

const uniqBy_ = require('lodash/uniqBy')

const {
  ADDRESSINPUT_ROLE,
  GRID_ROLE,
  DROPDOWN_OPTIONS_ROLE,
  GALLERY_ROLE,
  MEDIA_GALLERY_ROLE,
  UPLOAD_BUTTON_ROLE,
  DETAILS_DATASET_ROLE,
  REPEATER_ROLE,
  PAGINATION_ROLE,
  DETAILS_REPEATER_ROLE,
  RATINGSDISPLAY_ROLE,
  RATINGSINPUT_ROLE
} = require('dbsm-common/src/connection-config/roles')

const { createAdapterContext } = require('./context')

const { getScopeType, SCOPE_TYPES } = require('./scopeTypes')
const { findContainer } = require('./findContainers')

const defaultAdapter = require('./adapters/defaultAdapter')
const gridAdapter = require('./adapters/gridAdapter')
const dropdownOptionsAdapter = require('./adapters/dropdownOptionsAdapter')
const galleryAdapter = require('./adapters/galleryAdapter')
const uploadButtonAdapter = require('./adapters/uploadButtonAdapter')
const nullAdapter = require('./adapters/nullAdapter')
const connectedRepeaterAdapter = require('./adapters/connectedRepeaterAdapter')
const detailsRepeaterAdapter = require('./adapters/detailsRepeaterAdapter')
const paginationAdapter = require('./adapters/paginationAdapter')

/*::
import type {
  Component,
  ConnectionConfig,
  Selector,
  WixSdk
} from '../viewer-app-module/platformTypes'
import type { GetFieldType } from '../schemas/types'
import type { AdapterApi, AdapterParameters, ComponentAdapterContext } from './types'

type ComponentAdapterContextsCreatorArgs = {
  connectedComponents: Array<{ component: Component, role: string }>,
  adapterApi: () => AdapterApi,
  getFieldType: GetFieldType,
  ignoreItemsInRepeater: boolean,
  $w: Selector,
  dependencies: any,
  adapterParams: AdapterParameters
}

type DetailsRepeaterAdapterContextsCreator = (
  connectedComponents: Array<{ component: Component, role: string }>,
  getFieldType: GetFieldType,
  dependencies: any,
  adapterParams: AdapterParameters
) => ComponentAdapterContext[]
*/

const rolesToApiCreators = {
  default: defaultAdapter,
  [GRID_ROLE]: gridAdapter,
  [DROPDOWN_OPTIONS_ROLE]: dropdownOptionsAdapter,
  [GALLERY_ROLE]: galleryAdapter,
  [MEDIA_GALLERY_ROLE]: defaultAdapter,
  [UPLOAD_BUTTON_ROLE]: uploadButtonAdapter,
  [DETAILS_DATASET_ROLE]: nullAdapter,
  [REPEATER_ROLE]: connectedRepeaterAdapter,
  [PAGINATION_ROLE]: paginationAdapter,
  [DETAILS_REPEATER_ROLE]: detailsRepeaterAdapter,
  [RATINGSDISPLAY_ROLE]: defaultAdapter,
  [RATINGSINPUT_ROLE]: defaultAdapter
}

const createComponentAdapter = ({ role, adapterParams }) => {
  if (
    role === RATINGSDISPLAY_ROLE ||
    role === RATINGSINPUT_ROLE ||
    role === ADDRESSINPUT_ROLE ||
    role === MEDIA_GALLERY_ROLE
  ) {
    return defaultAdapter(adapterParams, { shouldConvertToString: false })
  }

  const apiCreator = rolesToApiCreators[role] || rolesToApiCreators.default
  return apiCreator(adapterParams)
}

const createComponentAdapterContexts = (
  {
    connectedComponents,
    adapterApi,
    getFieldType,
    ignoreItemsInRepeater,
    $w,
    dependencies,
    adapterParams
  } /*: ComponentAdapterContextsCreatorArgs */
) /*: ComponentAdapterContext[] */ => {
  const cacs = []

  connectedComponents.forEach(({ component, role }) => {
    if (ignoreItemsInRepeater) {
      const isComponentInsidePrimaryOrDetailsRepeater = findContainer(component)
        .map(container =>
          getScopeType(container.uniqueId, dependencies, connectedComponents)
        )
        .map(
          scopeType =>
            scopeType === SCOPE_TYPES.PRIMARY ||
            scopeType === SCOPE_TYPES.DETAILS
        )
        .getOrElse(false)

      if (isComponentInsidePrimaryOrDetailsRepeater) {
        return
      }
    }

    const adapterContext = createAdapterContext({
      getFieldType,
      role,
      component,
      $w,
      api: createComponentAdapter({ role, adapterParams })
    })

    if (adapterApi().isValidContext(adapterContext)) {
      cacs.push(adapterContext)
    }
  })

  return cacs
}

const createDetailsRepeatersAdapterContexts /*: DetailsRepeaterAdapterContextsCreator */ = (
  connectedComponents,
  getFieldType,
  dependencies,
  adapterParams
) => {
  const detailsRepeaters = []

  connectedComponents.forEach(({ component }) => {
    findContainer(component).chain(container => {
      const scopeType = getScopeType(
        container.uniqueId,
        dependencies,
        connectedComponents
      )
      if (scopeType === SCOPE_TYPES.DETAILS) {
        detailsRepeaters.push(container)
      }
    })
  })

  const uniqDetailsRepeaters = uniqBy_(detailsRepeaters, 'id')
  return uniqDetailsRepeaters.map(detailsRepeater =>
    createAdapterContext({
      getFieldType,
      role: DETAILS_REPEATER_ROLE,
      component: detailsRepeater,
      api: createComponentAdapter({
        role: DETAILS_REPEATER_ROLE,
        adapterParams
      })
    })
  )
}

module.exports.createComponentAdapterContexts = createComponentAdapterContexts
module.exports.createDetailsRepeatersAdapterContexts = createDetailsRepeatersAdapterContexts



// WEBPACK FOOTER //
// ./components/createComponentAdapter.js